import React, { useId } from 'react';
import { Box } from '@apia/theme';
import focus from '../classes/focusController';
import { snakeToKebab } from './util';
import EventEmitter from './util/eventEmitter';

export function makeCustomEvent(eventType: keyof typeof customEvents) {
  return new CustomEvent(customEvents[eventType], { bubbles: true });
}

/**
 * Existen algunos eventos que se disparan en la
 * aplicación con la finalidad de desencadenar
 * un comportamiento específico. Por ello se declaran
 * las siguientes constantes.
 */
export const customEvents = {
  /**
   * Inicialmente, su objetivo es el de borrar el error en un filtro.
   */
  clearError: 'clearError',
  /**
   * Indica que un elemento necesita recibir el foco,
   * de esta forma, elementos que no se están mostrando
   * en pantalla (por display:none por ejemplo), pueden
   * indicar a sus ancestros que deben expandirse.
   */
  focus: 'customFocus',
  /**
   * Indica que debe cambiarse el título del modal
   */
  iframeModalChangeTitle: 'iframeModalChangeTitle',
  /**
   * Indica que un iframe modal debe cerrarse
   */
  iframeModalClose: 'iframeModalClose',
  /**
   * Indica que un iframe modal debe cerrarse
   */
  iframeModalNotify: 'iframeModalNotify',
  /**
   * Indica que un modal debe cerrarse
   */
  modalClose: 'modalClose',
  /**
   * Indica que el panel contenedor debe ocultarse porque
   * está vacío
   */
  hidePanel: 'hidePanel',
  showPanel: 'showPanel',
  /**
   * Indica que el panel contenedor debe cerrarse porque
   * está vacío
   */
  collapsePanel: 'collapsePanel',
  expandPanel: 'expandPanel',
  /**
   * Evento lanzado por formsEventsController sobre window
   * cuando se termina de procesar los formularios de la tarea
   */
  formsReady: 'formsReady',
};

/**
 * Permite iterar desde un elemento hacia arriba en el
 * DOM, utilizando una función de comparación para
 * determinar si el elemento actual es el que estamos
 * buscando.
 *
 * Si la función checkParent devuelve true, isChild
 * devuelve true.
 */
export function isChild(
  element: HTMLElement | null,
  checkParent: (parent: HTMLElement) => boolean | null,
) {
  let currentElement = element;

  while (element !== document.documentElement && currentElement) {
    const isParent = checkParent(currentElement);
    if (isParent) return true;
    if (isParent === null) return false;
    const parent = currentElement.parentElement;
    if (parent) currentElement = parent;
    else return false;
  }

  return false;
}

/**
 * Searches for a specific parent of a element,
 * using a callback function as the source of
 * truth in order to determine which is the correct
 * parent.
 *
 * @param element The element where the process
 * starts.
 *
 * @param checkParent A callback that will be
 * called for each of the ancestors of the element
 * until the desired parent is found.
 * This function should return **true when found**
 * the desired parend was found, **null to cancel**
 * the search or **false to continue searching**.
 *
 * @returns The specific parent or null in case
 * the root parent element is raised.
 */
export function getSpecificParent(
  element: HTMLElement | null,
  checkParent: (parent: HTMLElement) => boolean | null,
): HTMLElement | null {
  let currentElement = element;

  while (element !== document.documentElement && currentElement) {
    const hasFoundTheParent = checkParent(currentElement);
    if (hasFoundTheParent === null) return null;
    if (hasFoundTheParent === true) return currentElement;

    const parent = currentElement.parentElement;
    if (parent) currentElement = parent;
    else return null;
  }

  return null;
}

/**
 * Searches for a specific parent of a element,
 * using a callback function as the source of
 * truth in order to determine which is the correct
 * parent.
 *
 * @param element The element where the process
 * starts.
 *
 * @param checkParent A callback that will be
 * called for each of the ancestors of the element
 * until the desired parent is found.
 * This function should return **true when found**
 * the desired parend was found, **null to cancel**
 * the search or **false to continue searching**.
 *
 * @returns The specific parent or null in case
 * the root parent element is raised.
 */
export const findParent = getSpecificParent;

/**
 * Este método fue reemplazado por focus.on
 *
 * @see focus
 */
export function enforceFocus(
  element: HTMLElement | (() => HTMLElement | null | false),
  props?: {
    scrollIntoViewOptions?: ScrollIntoViewOptions;
  },
) {
  return focus.on(element, {
    scrollIntoViewOptions: props?.scrollIntoViewOptions,
  });
}
export const notUnfocusableSelector = [
  '[disabled]',
  '[tabIndex="-1"]',
  '[aria-hidden="true"]',
  '[readonly]',
  '[data-focus-guard]',
  '.modal__closeButton',
]
  .map((current) => `:not(${current})`)
  .join('');
export const focusSelector = [
  'input',
  'textarea',
  'select',
  'a',
  'button:not(.toggleAccordionElement)',
  '[contenteditable]',
  '[tabIndex]',
  '[role="button"]',
]
  .map((current) => `${current}${notUnfocusableSelector}`)
  .join(',');

/**
 * Genera un selector que permitirá seleccionar
 * todos los elementos candidatos a recibir foco,
 * que no cumplan con la condición not pasada
 * como parámetro.
 *
 * @param not Un selector not css, indicando cuáles
 * condiciones deben evitar el foco en un elemento.
 *
 * @returns Una cadena que representa un selector
 * css.
 */
export function getFocusSelector(not?: string) {
  return `input${not ?? ''}, 
    textarea${not ?? ''}, 
    select${not ?? ''}, 
    a${not ?? ''}, 
    button:not(.toggleAccordionElement)${not ?? ''}, 
    [contenteditable]${not ?? ''}, 
    [tabIndex]${not ?? ''}`;
}

const previousTabIndexLabel = 'data-previous-tabindex';

/**
 * Deshabilita la posibilidad de hacer foco en
 * todos los descendientes de parent. Es
 * especialmente útli cuando se ocultan elementos
 * del DOM sin quitarlos, cuando por accesibilidad
 * es importante que el foco no alcance a dichos
 * métodos.
 */
export function disableChildrenFocus(parent: HTMLElement) {
  const focusElement = parent.querySelectorAll(focusSelector);
  focusElement.forEach((element) => {
    element.setAttribute(
      previousTabIndexLabel,
      element.getAttribute('tabindex') ?? '',
    );
    element.setAttribute('tabindex', '-1');
  });
}

/**
 * Restaura el foco de los hijos de parent a los
 * que previamente se les había quitado el foco
 * con disableChildrenFocus.
 */
export function enableChildrenFocus(parent: HTMLElement) {
  const focusElement = parent.querySelectorAll(`[${previousTabIndexLabel}]`);
  focusElement.forEach((element) => {
    element.setAttribute(
      'tabindex',
      element.getAttribute(previousTabIndexLabel) ?? '0',
    );
    element.removeAttribute(previousTabIndexLabel);
  });
}

const REGEXP_SCROLL_PARENT = /^(visible|hidden)/;
/**
 * Encuentra el primer elemento padre del elemento provisto que tenga scroll
 * activo.
 */
export function findScrollContainer(el: HTMLElement): HTMLElement | null {
  return !(el instanceof HTMLElement) ||
    typeof window.getComputedStyle !== 'function'
    ? null
    : el.scrollHeight >= el.clientHeight &&
        !REGEXP_SCROLL_PARENT.test(
          window.getComputedStyle(el).overflowY || 'visible',
        )
      ? el
      : findScrollContainer(el.parentElement as HTMLElement) || document.body;
}

/**
 * Esta función calcula el offset que existe entre un elemento y su padre más
 * inmediato que tenga scroll visible
 */
export function findOffsetRelativeToScrollParent(
  element: HTMLElement,
  which: 'Left' | 'Top' = 'Top',
) {
  const scrollParent = findScrollContainer(element);
  let currentElement = element;
  let offset = 0;
  let offsetParent = currentElement;
  while (
    currentElement &&
    currentElement !== scrollParent &&
    currentElement !== document.body &&
    offsetParent !== scrollParent
  ) {
    if (offsetParent !== currentElement.offsetParent) {
      offset += currentElement[`offset${which}`];
      offsetParent = currentElement.offsetParent as HTMLElement;
    }
    currentElement = currentElement.parentElement as HTMLElement;
  }
  return offset;
}

const scrollIntervals: Record<string, number> = {};
/**
 * Busca el padre con scroll visible y establece su scrollTop de acuerdo al
 * offset del elemento pasado como argumento, de modo que éste aparezca visible
 * dentro del padre mencionado.
 *
 * @param fixedOffsetTop Establece un mínimo de distancia que debe mantener con el borde superior del contenedor con scroll. Se usa para compensar en las situaciones donde existen cabezales fijos.
 */
export function scrollParentIntoElement(
  element: HTMLElement,
  fixedOffsetTop = 0,
  tries = 2,
  timeout = 100,
  scrollId: string | undefined = undefined,
) {
  if (scrollId) {
    clearTimeout(scrollIntervals[scrollId]);
  }

  const scrollParent = findScrollContainer(element);
  const offsetTop = findOffsetRelativeToScrollParent(element);

  if (
    scrollParent &&
    (scrollParent.scrollTop + fixedOffsetTop > offsetTop ||
      (scrollParent?.clientHeight ?? Infinity) - fixedOffsetTop <
        element.getBoundingClientRect().height)
  ) {
    scrollParent.scrollTop = offsetTop - fixedOffsetTop;
  } else if (
    scrollParent &&
    scrollParent.scrollTop + scrollParent.clientHeight <
      offsetTop + element.getBoundingClientRect().height
  ) {
    scrollParent.scrollTop = offsetTop;
  }

  if (tries > 0)
    scrollIntervals[scrollId ?? 'noId'] = setTimeout(
      () =>
        scrollParentIntoElement(element, fixedOffsetTop, tries - 1, timeout),
      timeout,
    ) as unknown as number;
}

export interface IAutoDisconnectMutationObserverConf {
  /** Por defecto es true */
  runCallbackOnInit?: boolean;
  /** Por defecto es 100 (ms) */
  timeout?: number;
}

/**
 * Crea un observer que va a desconectarse automáticamente luego de que pase
 * una determinada cantidad de tiempo sin actividad en el elemento.
 *
 * Se usa especialmente para esperar que un componente termine de renderizar y
 * aplicar cálculos a nivel de layout sobre él.
 */
export function autoDisconnectMutationObserver(
  element: HTMLElement,
  callback: () => unknown,
  conf?: IAutoDisconnectMutationObserverConf,
) {
  let timeoutRegister = -1;
  let isConnected = false;
  function disconnect() {
    if (isConnected) {
      isConnected = false;
      observer.disconnect();
      clearTimeout(timeoutRegister);
    }
  }
  let shoutCallback: number;

  const observer = new MutationObserver((...props) => {
    if (props[0]?.[0] && (props[0][0].removedNodes || props[0][0].addedNodes)) {
      clearTimeout(shoutCallback);
      shoutCallback = setTimeout(callback, 100) as unknown as number;
      clearTimeout(timeoutRegister);
      timeoutRegister = setTimeout(
        disconnect,
        conf?.timeout ?? 100,
      ) as unknown as number;
    }
  });
  isConnected = true;
  observer.observe(element, { subtree: true, childList: true });
  timeoutRegister = setTimeout(
    disconnect,
    conf?.timeout ?? 100,
  ) as unknown as number;

  if (conf?.runCallbackOnInit !== false)
    shoutCallback = setTimeout(callback, 100) as unknown as number;

  return disconnect;
}

/**
 * Construye un selector css lo más específico posible
 */
export function buildSelector(element: HTMLElement) {
  if (element?.id === 'tinymce') {
    const documentElement = element.ownerDocument;
    const fieldId = documentElement.body.className.match(
      /((?:P|E)_\d{4,5}_\d+)/,
    )?.[1];
    if (fieldId) {
      const parentWindow = documentElement.defaultView?.parent;
      if (parentWindow) {
        // eslint-disable-next-line no-param-reassign
        element = parentWindow.document.querySelector(
          `#${fieldId}`,
        ) as HTMLElement;
      }
    }
  }
  const name =
    (element as HTMLInputElement)?.name || element?.dataset.name || '';
  if (!name && element) {
    const childElement = element.querySelector('[name]');
    if (childElement) {
      // eslint-disable-next-line no-param-reassign
      element = childElement as HTMLElement;
    } else {
      const parent = element.parentElement;
      const siblingElement = parent?.querySelector('[name]');
      // eslint-disable-next-line no-param-reassign
      if (siblingElement) element = siblingElement as HTMLElement;
    }
  }
  const selectors = [];

  if (element?.tagName) selectors.push(element.tagName.toLowerCase());

  if (element?.classList)
    selectors.push(
      [...(element?.classList ?? [])]
        .filter((current) => !current.match(/^css-/))
        .map((current) => `.${current}`)
        .join(''),
    );

  if (element?.dataset.name)
    selectors.push(`[data-name="${element.dataset.name}"]`);
  if ((element as HTMLInputElement)?.name)
    selectors.push(`[name="${(element as HTMLInputElement).name}"]`);

  const ariaLabel = element?.attributes.getNamedItem('aria-label')?.value;
  if (ariaLabel) selectors.push(`[aria-label="${ariaLabel}"]`);

  ['att-id', 'field-id', 'idx'].forEach((dataName) => {
    const data = element?.dataset[dataName];
    if (data) selectors.push(`[data-${snakeToKebab(dataName)}="${data}"]`);
  });

  if (element?.id) selectors.push(`#${element.id}`);

  return selectors.join('');
}

type TLiveEvent = {
  message: string;
  type: 'polite' | 'assertive' | 'reset';
};

type TLive = {
  message: string;
  type: 'polite' | 'assertive';
};

/**
 * Este método se utiliza para emitir mensajes
 * a los usuarios con discapacidades. **IMPORTANTE:
 * El empleo de esta herramienta debe ser analizado
 * a conciencia**.
 *
 * **La forma que considero correcta de analizar el
 * problema es:**
 *
 * Si yo fuera un usuario ciego o discapacitado, qué
 * tipo de mensaje me gustaría recibir para entender
 * el uso de las herramientas en el contexto actual.
 *
 * Si se encuentra que esta forma de analizar el
 * problema tiene fallos o carencias, por favor
 * iniciar la discusión correspondiente ya que la idea
 * es ir mejorando esta herramienta.
 *
 * **El uso es muy sencillo:**
 *
 * Al llamar al método
 * AriaLiveEmitter.emit({ live: 'polite' | 'assertive',
 * message: 'un mensaje' }), se actualiza un div para
 * que contenta estas características, lo que termina
 * leyendo dicho mensaje al usuario.
 */
export const AriaLiveEmitter =
  new (class AriaLiveEmitterClass extends EventEmitter<{
    live: TLiveEvent;
  }> {})();

export const LiveRegion = () => {
  const [live, setLive] = React.useState<TLive>({
    type: 'assertive',
    message: '',
  });

  React.useEffect(() => {
    return AriaLiveEmitter.on('live', (ev) => {
      if (ev.type === 'reset') {
        setLive({ type: 'assertive', message: '' });
        return;
      }
      if (ev.message === live.message) return;
      setTimeout(() => setLive(ev as TLive), 0);
    });
  }, [live.message]);

  const id = useId();

  return (
    <Box
      aria-label={id}
      role="region"
      sx={{ transform: 'translate(-50000px, -50000px)' }}
      aria-live={live.type}
    >
      {live.message}
    </Box>
  );
};
